import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Building2, CheckCircle2, Copy, Phone, PhoneCall } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useTheme } from '../contexts/ThemeContext';
import { useScreenInit } from '../useScreenInit.js';
interface ForwardingSetupScreenProps {
  onBack: () => void;
  onChooseMobile: () => void;
  onLandlineActivated: () => void;
}
const VEXU_NUMBER = '+1 (555) 019-8472';
export function ForwardingSetupScreen({
  onBack,
  onChooseMobile,
  onLandlineActivated
}: ForwardingSetupScreenProps) {
  const {
    isLight
  } = useTheme();
  const screenInit = useScreenInit();
  const [selection, setSelection] = useState<'mobile' | 'landline' | null>(screenInit.selection === 'landline' || screenInit.selection === 'mobile' ? screenInit.selection : null);
  const [copied, setCopied] = useState(false);
  const copyForwardingNumber = () => {
    navigator.clipboard.writeText(VEXU_NUMBER);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };
  const isLandline = selection === 'landline';
  return <div className={`h-full flex flex-col overflow-hidden ${isLight ? 'bg-stone-100' : 'bg-black'}`} data-id="element-2879" data-mp="1zav8ownasa57">
      <header className="px-6 pt-6 pb-4 flex items-center gap-3" data-id="element-2880" data-mp="24a7sf8ivh13k">
        <button onClick={onBack} aria-label="Back" className={`p-2 -ml-2 rounded-full transition-colors ${isLight ? 'bg-white text-slate-500 border border-slate-200 hover:text-slate-900' : 'bg-white/5 text-zinc-400 border border-white/10 hover:text-white'}`} data-id="element-2881" data-mp="294c2w1rcjr9e">
          <ArrowLeft className="w-5 h-5" data-id="element-2882" data-mp="2n31y1bulvgv4" />
        </button>
        <div data-id="element-2883" data-mp="2s254n0q8hv57">
          <p className={`text-[11px] font-bold uppercase tracking-wider ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-2884" data-mp="2s0kb9efnoi8x">
            One last step
          </p>
          <h1 className={`text-lg font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2885" data-mp="xn2jff87wz83">
            Connect a number
          </h1>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-6 pb-8" data-id="element-2886" data-mp="llkrkguoeai1">
        <AnimatePresence mode="wait" data-id="element-2887" data-mp="lmvygpjf3ydd">
          {!isLandline ? <motion.div key="number-choice" initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -12
        }} transition={{
          duration: 0.22
        }} className="pt-7" data-id="element-2888" data-mp="zlc9uts86d1u">
              <div className="mb-8" data-id="element-2889" data-mp="1ideknoi6qz46">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-2890" data-mp="1dc7eftvoy7zm">
                  <PhoneCall className="w-7 h-7" data-id="element-2891" data-mp="1nbg44ajix0g6" />
                </div>
                <h2 className={`text-2xl font-bold tracking-tight mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2892" data-mp="26wgl99abasfc">
                  Which number should Vexu answer?
                </h2>
                <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2893" data-mp="1wz27jhfrwhqq">
                  Choose the number you want to forward to your AI assistant.
                  We’ll show the right setup instructions next.
                </p>
              </div>

              <div className="space-y-3" data-id="element-2894" data-mp="1rzzcmptibrwh">
                <button onClick={() => setSelection('mobile')} className={`w-full p-4 rounded-2xl text-left border transition-colors ${isLight ? 'bg-white border-slate-200 hover:border-vexu/50 hover:bg-vexu/[0.02] shadow-sm' : 'bg-white/[0.04] border-white/10 hover:border-vexu/50'}`} data-id="element-2895" data-mp="1e7rjd81fmj2j">
                  <div className="flex items-start gap-3.5" data-id="element-2896" data-mp="197mzr9589883">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-2897" data-mp="2cjlzceh2ht3i">
                      <Phone className="w-5 h-5" data-id="element-2898" data-mp="22t95twr9ypl8" />
                    </div>
                    <div data-id="element-2899" data-mp="1xupjhv8rrbsc">
                      <p className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2900" data-mp="1xv7ll8i44k86">
                        Mobile number
                      </p>
                      <p className={`text-xs leading-relaxed mt-1 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2901" data-mp="1osyeksm1z8zj">
                        Set up the answering rules you want, such as when busy
                        or unanswered.
                      </p>
                    </div>
                  </div>
                </button>

                <button onClick={() => setSelection('landline')} className={`w-full p-4 rounded-2xl text-left border transition-colors ${isLight ? 'bg-white border-slate-200 hover:border-vexu/50 hover:bg-vexu/[0.02] shadow-sm' : 'bg-white/[0.04] border-white/10 hover:border-vexu/50'}`} data-id="element-2902" data-mp="399cwymknnwdj">
                  <div className="flex items-start gap-3.5" data-id="element-2903" data-mp="3qjp2d378hjv0">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-zinc-300'}`} data-id="element-2904" data-mp="3vijmc8gxtegp">
                      <Building2 className="w-5 h-5" data-id="element-2905" data-mp="32ue1llpvvpgv" />
                    </div>
                    <div data-id="element-2906" data-mp="37sfs2rpn2r2d">
                      <p className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2907" data-mp="37r4ho91oul2a">
                        Landline number
                      </p>
                      <p className={`text-xs leading-relaxed mt-1 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2908" data-mp="n4kspdhfu56d">
                        Forward all calls from your landline with one simple
                        code.
                      </p>
                    </div>
                  </div>
                </button>
              </div>

              <p className={`text-[11px] leading-relaxed text-center mt-6 px-4 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2909" data-mp="2x4l4hhq91zbr">
                You can manage or change your forwarding setup later.
              </p>
            </motion.div> : <motion.div key="landline-guide" initial={{
          opacity: 0,
          x: 18
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -18
        }} transition={{
          duration: 0.22
        }} className="pt-5" data-id="element-2910" data-mp="20goks3zimnwb">
              <button onClick={() => setSelection(null)} className={`text-sm font-semibold mb-7 ${isLight ? 'text-slate-500 hover:text-slate-800' : 'text-zinc-400 hover:text-white'}`} data-id="element-2911" data-mp="17k5mbtpu3gfm">
                ← Choose a different number
              </button>

              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-2912" data-mp="12l2q8yru0kjs">
                <Building2 className="w-7 h-7" data-id="element-2913" data-mp="1v9zw22euwovn" />
              </div>
              <h2 className={`text-2xl font-bold tracking-tight mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2914" data-mp="1v86zjfxyjl1d">
                Forward your landline
              </h2>
              <p className={`text-sm leading-relaxed mb-6 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2915" data-mp="1ldwwn4be34ce">
                Use the handset connected to the landline you’d like Vexu to
                answer.
              </p>

              <div className="space-y-3 mb-6" data-id="element-2916" data-mp="3ncl6ibmen24v">
                {['Pick up your landline handset.', 'Dial *72, then enter your Vexu number.', 'Wait for your provider’s confirmation tone or message.'].map((step, index) => <div key={step} className={`flex items-start gap-3 p-3.5 rounded-2xl ${isLight ? 'bg-white border border-slate-200 shadow-sm' : 'bg-white/[0.04] border border-white/10'}`} data-id="element-2917" data-mp="39dc2eabh3yh9" data-mpi={index}>
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[11px] font-bold ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-2918" data-mp="72mhuwidakd">
                      {index + 1}
                    </span>
                    <p className={`text-sm leading-relaxed pt-0.5 ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-2919" data-mp="nwojcbperdp7">
                      {step}
                    </p>
                  </div>)}
              </div>

              <div className={`rounded-2xl p-4 mb-4 ${isLight ? 'bg-vexu/5 border border-vexu/20' : 'bg-vexu/10 border border-vexu/20'}`} data-id="element-2920" data-mp="2birhd555q9kc">
                <p className={`text-[11px] font-bold uppercase tracking-wider mb-2 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-2921" data-mp="2kjz5wvhvfw18">
                  Dial from your landline
                </p>
                <div className="flex items-center justify-between gap-3" data-id="element-2922" data-mp="2ufj2lk49eyr1">
                  <code className={`text-base font-bold tracking-wide ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2923" data-mp="2z9dgu813iliq">
                    *72 {VEXU_NUMBER}
                  </code>
                  <button onClick={copyForwardingNumber} aria-label="Copy Vexu number" className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${copied ? 'bg-vexu text-white' : isLight ? 'bg-white text-slate-500 border border-slate-200' : 'bg-white/10 text-zinc-300'}`} data-id="element-2924" data-mp="38alcsjg53a9n">
                    {copied ? <CheckCircle2 className="w-4 h-4" data-id="element-2925" data-mp="1iuu82a8kyf9c" /> : <Copy className="w-4 h-4" data-id="element-2926" data-mp="1np87cnqd56ja" />}
                  </button>
                </div>
              </div>

              <div className={`rounded-2xl px-4 py-3.5 mb-7 ${isLight ? 'bg-slate-100 border border-slate-200' : 'bg-white/[0.04] border border-white/10'}`} data-id="element-2927" data-mp="1xlurfaav7tk9">
                <p className={`text-xs font-bold mb-1 ${isLight ? 'text-slate-700' : 'text-zinc-200'}`} data-id="element-2928" data-mp="22j3e9o6yklru">
                  To turn it off later
                </p>
                <p className={`text-xs leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2929" data-mp="899gqqcsht6s">
                  Dial <code className="font-bold" data-id="element-2930" data-mp="4q0ud65g7e0e">*73</code> from the same
                  landline to deactivate call forwarding.
                </p>
              </div>

              <Button fullWidth onClick={onLandlineActivated} data-id="element-2931" data-mp="3r4aide5guk0n">
                I’ve activated forwarding
              </Button>
            </motion.div>}
        </AnimatePresence>
      </div>

      {!isLandline && selection === 'mobile' && <div className="px-6 pb-8" data-id="element-2932" data-mp="nnbko193qx3p">
          <Button fullWidth onClick={onChooseMobile} data-id="element-2933" data-mp="ioqrumw6jfpa">
            Continue with mobile
          </Button>
        </div>}
    </div>;
}